import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames/bind';

import { extractColorValue, getContrastTextColor } from '@HELPERS/colorUtils';
import styles from './TabInterface.module.scss';

const cx: CX = classnames.bind(styles);

interface Tab {
    id: string;
    label: string;
    content: React.ReactNode;
    icon?: React.ReactNode;
    backgroundColor?: string; // Add optional background color prop
}

interface TabInterfaceProps {
    tabs: Tab[];
    defaultTab?: string;
    className?: string;
    variant?: 'default' | 'footer'; // Add variant support
    showBottomShadow?: boolean; // Add option for bottom shadow
}

function TabInterface(props: TabInterfaceProps) {
    const {
        tabs,
        defaultTab,
        className,
        variant = 'default',
        showBottomShadow = false,
    } = props;

    const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);
    const [isUserInitiated, setIsUserInitiated] = useState(false);

    // Reset user-initiated flag when defaultTab prop changes to allow programmatic updates
    useEffect(() => {
        setIsUserInitiated(false);
    }, [defaultTab]);

    // Update activeTab when defaultTab prop changes, but only if user hasn't manually clicked a tab
    useEffect(() => {
        if (defaultTab && defaultTab !== activeTab && !isUserInitiated) {
            setActiveTab(defaultTab);
        }
    }, [defaultTab, activeTab, isUserInitiated]);

    const handleTabClick = (tabId: string) => {
        setIsUserInitiated(true);
        setActiveTab(tabId);
    };

    const handleKeyDown = (event: React.KeyboardEvent, tabId: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsUserInitiated(true);
            setActiveTab(tabId);
        }
    };

    const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

    // Helper function to get dynamic styles for active tab
    const getActiveTabStyles = (tab: Tab) => {
        if (tab.backgroundColor) {
            // Use custom background color if provided
            const backgroundColor = extractColorValue(tab.backgroundColor);

            // Ensure we have a valid hex color
            if (!backgroundColor || (!backgroundColor.startsWith('#') && !backgroundColor.match(/^rgb/))) {
                return {
                    background: tab.backgroundColor,
                    color: '#ffffff', // Default to white text for unknown colors
                };
            }

            const textColor = getContrastTextColor(backgroundColor);

            return {
                background: backgroundColor,
                color: textColor,
                '--custom-bg': backgroundColor,
                '--custom-color': textColor,
            } as React.CSSProperties;
        }

        // Calculate text color based on the default gradient background
        // Extract the primary color from CSS variables to determine text color
        const primaryColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--marriott-primary')
            .trim() || '#00a4ba';

        const textColor = getContrastTextColor(primaryColor);

        return {
            '--auto-text-color': textColor,
        } as React.CSSProperties;
    };

    return (
        <div className={cx('tab-interface', variant, className, {
            'show-bottom-shadow': showBottomShadow,
        })}
        >
            <div className={cx('tab-navigation')} role="tablist">
                <div className={cx('tab-list')}>
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        const dynamicStyles = isActive ? getActiveTabStyles(tab) : {};

                        return (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                aria-controls={`tabpanel-${tab.id}`}
                                id={`tab-${tab.id}`}
                                tabIndex={isActive ? 0 : -1}
                                className={cx('tab-button', {
                                    active: isActive,
                                    'custom-background': isActive && tab.backgroundColor, // Add class for custom background
                                })}
                                style={dynamicStyles}
                                onClick={() => handleTabClick(tab.id)}
                                onKeyDown={(e) => handleKeyDown(e, tab.id)}
                            >
                                {tab.icon && (
                                    <span className={cx('tab-icon')} aria-hidden="true">
                                        {tab.icon}
                                    </span>
                                )}
                                <span className={cx('tab-label')}>
                                    {tab.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div
                className={cx('tab-content')}
                role="tabpanel"
                id={`tabpanel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
            >
                {activeTabContent}
            </div>
        </div>
    );
}

TabInterface.defaultProps = {
    defaultTab: undefined,
    className: '',
    variant: 'default',
    showBottomShadow: false,
};

TabInterface.propTypes = {
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            content: PropTypes.node.isRequired,
            icon: PropTypes.node,
            backgroundColor: PropTypes.string, // Add background color prop
        }),
    ).isRequired,
    defaultTab: PropTypes.string,
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'footer']),
    showBottomShadow: PropTypes.bool,
};

export default TabInterface;
